import React from "react";
import { Accordion, Alert, Button, Card, Form } from "react-bootstrap";
import CamplaCoreService from "../../../../services/CamplaCoreService";
import ValidationHelper from "../../../../helpers/ValidationHelper";
import { withTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import MarkDownEditor from "../../../../components/markdownEditor/MarkDownEditor";
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-powershell";
import "prismjs/components/prism-bash";
import "prismjs/components/prism-clike";
import Editor from "react-simple-code-editor";
import "prismjs/themes/prism.css";
import "./CreateScriptDialog.css";
import BaseDialog from "../../../../components/dialogs/baseDialog/BaseDialog";
import BaseSelect from "../../../../components/baseSelect/BaseSelect";

class CreateScriptDialog extends React.Component {
  constructor(props) {
    super(props);
    this.validationDefinition = {
      name: {
        defaultValue: "",
        initValid: false,
        rule: value => ValidationHelper.isNonEmptyStringOfLength(value, 3, 50)
      },
      description: {
        defaultValue: ValidationHelper.getDefaultValueExtendedDescription(),
        initValid: false,
        rule: ValidationHelper.isNonEmptyExtendedDescription
      },
      type: {
        defaultValue: undefined,
        initValid: false,
        rule: value => ValidationHelper.isIntInRange(value, 0, 1)
      },
      script: {
        defaultValue: this.getPlaceholderScript(),
        initValid: false,
        rule: ValidationHelper.isNonEmptyString
      }
    };

    this.state = {
      validation: ValidationHelper.getValidation(this.validationDefinition)
    };

    this.handleChange = this.handleChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onHide = this.onHide.bind(this);
  }

  getHiglightingLanguage(selectedLanguage) {
    switch (selectedLanguage) {
      case 0:
        return languages.bash;
      case 1:
      default:
        return languages.powershell;
    }
  }

  getPlaceholderScript() {
    return "";
  }

  onSubmit() {
    if (this.state.validation._isValid) {
      const validatedData = ValidationHelper.getData(this.state.validation);
      validatedData["userId"] = CamplaCoreService.getAuthenticatedUser()._id;
      this.props.onSubmit(validatedData);
    }
  }

  handleChange(event) {
    this.setState({
      validation: ValidationHelper.update(event, this.state.validation)
    });
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.show !== this.props.show) {
      this.setState({
        validation: ValidationHelper.getValidation(this.validationDefinition)
      });
    }
  }

  onHide() {
    this.props.onHide(ValidationHelper.hasChanged(this.state.validation));
  }

  render() {
    const { t } = this.props;
    return (
      <BaseDialog
        show={this.props.show}
        onHide={this.onHide}
        rightButtonCallback={this.onSubmit}
        title={t("Scripts.CreateModal.Title")}
        rightButtonText={t("Scripts.CreateModal.Form.Submit")}
        leftButtonText={t("DialogCancel")}
        rightButtonVariant="green"
        leftButtonVariant="black"
        leftButtonCallback={this.onHide}
        dialogApiError={this.props.dialogApiError}
        rightButtonDisabled={!this.state.validation._isValid}
        body={(<div>
          {this.props.validationResult?.length > 0 ? (
            <Alert variant="danger">
              <ul>{this.props.validationResult}</ul>
            </Alert>
          ) : (
            ""
          )}
          <Form onSubmit={this.onSubmit}>
            <Accordion defaultActiveKey="0">
              <Card>
                <Card.Header className="pt-0 pb-0 pl-2 pr-2 custom-card-header">
                  <Accordion.Toggle
                    as={Button}
                    variant="link"
                    className="acc-link"
                    eventKey="0"
                  >
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      className={
                        this.state.validation.name.isValid &&
                        this.state.validation.type.isValid
                          ? "display-none"
                          : "mr-2 color-red"
                      }
                    />
                    <span>{t("Scripts.CreateModal.Form.ToggleBasic")}</span>
                  </Accordion.Toggle>
                </Card.Header>
                <Accordion.Collapse eventKey="0">
                  <Card.Body>
                    <Form.Group controlId="formScriptName">
                      <Form.Label>
                        {t("Scripts.CreateModal.Form.ScriptName")}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        placeholder={t(
                          "Scripts.CreateModal.Form.ScriptNamePlaceholder"
                        )}
                        value={this.state.validation.name.value}
                        isInvalid={!this.state.validation.name.isValid}
                        onChange={this.handleChange}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t("Scripts.CreateModal.Form.ValidationError.Name")}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group controlId="formSwitchIsPublished">
                      <Form.Label>
                        {t("Scripts.CreateModal.Form.Type")}
                      </Form.Label>
                      <BaseSelect
                        options={[
                          { label: "Powershell Script", value: 0 },
                          { label: "Shell (Bash) Script", value: 1 }
                        ]}
                        menuPosition="fixed"
                        placeholder={t(
                          "Scripts.CreateModal.Form.TypePlaceholder"
                        )}
                        onChange={value => {
                          this.handleChange({
                            target: {
                              name: "type",
                              value: value.value
                            }
                          });
                        }}
                      />
                      <Form.Control
                        type="hidden"
                        isInvalid={!this.state.validation.type.isValid}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t("Scripts.CreateModal.Form.ValidationError.Type")}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
              <Card>
                <Card.Header className="pt-0 pb-0 pl-2 pr-2 custom-card-header">
                  <Accordion.Toggle
                    as={Button}
                    variant="link"
                    className="acc-link"
                    eventKey="1"
                  >
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      className={
                        this.state.validation.description.isValid
                          ? "display-none"
                          : "mr-2 color-red"
                      }
                    />
                    <span>{t("Scripts.CreateModal.Form.Description")}</span>
                  </Accordion.Toggle>
                </Card.Header>
                <Accordion.Collapse eventKey="1">
                  <Card.Body>
                    <MarkDownEditor
                      value={ValidationHelper.getDefaultValueExtendedDescription()}
                      onChange={value =>
                        this.handleChange({
                          target: {
                            name: "description",
                            value: value
                          }
                        })
                      }
                    />
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
              <Card>
                <Card.Header className="pt-0 pb-0 pl-2 pr-2 custom-card-header">
                  <Accordion.Toggle
                    as={Button}
                    variant="link"
                    className="acc-link"
                    eventKey="2"
                  >
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      className={
                        this.state.validation.script.isValid
                          ? "display-none"
                          : "mr-2 color-red"
                      }
                    />
                    <span>{t("Scripts.CreateModal.Form.Script")}</span>
                  </Accordion.Toggle>
                </Card.Header>
                <Accordion.Collapse eventKey="2">
                  <Card.Body>
                    <div className="container_editor_area">
                      <Editor
                        placeholder={t(
                          "Scripts.CreateModal.Form.ScriptPlaceholder"
                        )}
                        value={this.state.validation.script.value}
                        onValueChange={code =>
                          this.handleChange({
                            target: {
                              name: "script",
                              value: code
                            }
                          })
                        }
                        highlight={code =>
                          highlight(
                            code,
                            this.getHiglightingLanguage(
                              this.state.validation.type.value
                            )
                          )
                        }
                        padding={10}
                        className="container__editor"
                      />
                    </div>
                    <Form.Control
                      type="hidden"
                      isInvalid={!this.state.validation.script.isValid}
                    />
                    <Form.Control.Feedback type="invalid">
                      {t("Scripts.CreateModal.Form.ValidationError.Script")}
                    </Form.Control.Feedback>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            </Accordion>
          </Form>
        </div>)}
      />
    );
  }
}

export default withTranslation()(CreateScriptDialog);
